// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type Ref } from 'react';

import { getThemeColor, type MantineColor, type MantineSize, useMantineTheme, getSize } from '@mantine/core';

import { isNil } from 'helpers/isNotNil';
import { EVOLVEICON_SIZE_PREFIX } from 'styles/theme/variableResolver';

import { iconMap } from './icons';

export type EvolveIconName = keyof typeof iconMap;

export type EvolveIconProps = {
  ref?: Ref<HTMLSpanElement>;
  /**
   * If the passed in icon name is null,
   * nothing will be rendered
   */
  icon: EvolveIconName | null;
  className?: string;
  inline?: boolean;
  spanProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>;
  onClick?: React.MouseEventHandler<HTMLSpanElement>;
  /** @defaultValue 'md' */
  size?: MantineSize | string | number;
} & (
  | {
      light?: boolean;
      color?: never;
    }
  | {
      light?: never;
      color?: MantineColor;
    }
);

export const EvolveIcon = ({
  ref,
  icon,
  className,
  spanProps,
  inline = false,
  color = 'inherit',
  light,
  size: _size = 'md',
  ...props
}: EvolveIconProps) => {
  const theme = useMantineTheme();
  const size = getSize(_size, EVOLVEICON_SIZE_PREFIX, false) ?? _size;
  const dimensions = { width: size, height: size };

  if (isNil(icon)) return null;
  const Icon = iconMap[icon];
  return (
    <span
      ref={ref}
      data-testid={icon}
      {...spanProps}
      {...props}
      style={{
        ...dimensions,
        display: inline ? 'inline-flex' : 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: inline ? 'top' : undefined,
        ...spanProps?.style,
      }}
    >
      <Icon
        className={className}
        color={getThemeColor(light ? 'gray.6' : color.replace('primary', theme.primaryColor), theme)}
        {...dimensions}
      />
    </span>
  );
};
